import { PolicyError, type PolicyDecision, type PolicyIssue } from './types';

export interface MetricSnapshot {
  id: string;
  source: 'google_ads' | 'meta_ads' | 'shopify' | 'manual';
  accountId: string;
  periodStart: string;
  periodEnd: string;
  timezone: string;
  currency: string;
  attributionBasis: string;
  conversionEvent: string;
  mature: boolean;
  spendMinor: number | null;
  revenueMinor: number | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
}

function safeMetric(value: number | null, field: string, integer = false): number | null {
  if (value === null) return null;
  if (!Number.isFinite(value) || value < 0 || (integer && !Number.isSafeInteger(value))) throw new PolicyError([{ code: 'METRIC_INVALID', message: field + ' must be a nonnegative ' + (integer ? 'integer' : 'number') + ' or null.' }]);
  return value;
}

export function deriveMetrics(snapshot: MetricSnapshot): {
  ctr: number | null; cpcMinor: number | null; cpaMinor: number | null; roas: number | null;
} {
  const spend = safeMetric(snapshot.spendMinor, 'Spend', true), revenue = safeMetric(snapshot.revenueMinor, 'Revenue', true);
  const impressions = safeMetric(snapshot.impressions, 'Impressions', true), clicks = safeMetric(snapshot.clicks, 'Clicks', true);
  const conversions = safeMetric(snapshot.conversions, 'Conversions');
  const divide = (numerator: number | null, denominator: number | null) => numerator === null || denominator === null || denominator === 0 ? null : numerator / denominator;
  return { ctr: divide(clicks, impressions), cpcMinor: divide(spend, clicks), cpaMinor: divide(spend, conversions), roas: divide(revenue, spend) };
}

export function assessComparability(snapshots: MetricSnapshot[], metric: 'spend' | 'conversion' | 'revenue'): PolicyDecision {
  const issues: PolicyIssue[] = [];
  const add = (code: string, message: string) => issues.push({ code, message });
  const first = snapshots[0];
  if (!first) return { allowed: false, issues: [{ code: 'NO_DATA', message: 'No source snapshots are available.' }] };
  if (new Set(snapshots.map((item) => item.id)).size !== snapshots.length) add('DUPLICATE_SNAPSHOT', 'The same snapshot cannot be counted twice.');
  for (const item of snapshots) {
    if (item.currency !== first.currency) add('CURRENCY_MISMATCH', 'Currencies require an explicit conversion basis before comparison.');
    if (item.periodStart !== first.periodStart || item.periodEnd !== first.periodEnd || item.timezone !== first.timezone) add('PERIOD_MISMATCH', 'Reporting windows and timezones must match.');
    if (metric !== 'spend' && (item.attributionBasis !== first.attributionBasis || item.conversionEvent !== first.conversionEvent || item.source !== first.source)) add('ATTRIBUTION_MISMATCH', 'Conversion and revenue attribution sources are not interchangeable.');
    if (metric !== 'spend' && !item.mature) add('DATA_IMMATURE', 'The observation window is not mature.');
  }
  return { allowed: issues.length === 0, issues };
}

/** Aggregate only non-overlapping, unique rows supplied by the report query. Never sums attributed purchases. */
export function aggregateSpend(snapshots: MetricSnapshot[]): number | null {
  const result = assessComparability(snapshots, 'spend');
  if (!result.allowed) throw new PolicyError(result.issues);
  if (snapshots.some((item) => item.spendMinor === null)) return null;
  const total = snapshots.reduce((sum, item) => sum + (safeMetric(item.spendMinor, 'Spend', true) ?? 0), 0);
  if (!Number.isSafeInteger(total)) throw new PolicyError([{ code: 'METRIC_OVERFLOW', message: 'The aggregated amount exceeds safe precision.' }]);
  return total;
}

export function uniqueStoreOrders(orders: { storeId: string; orderId: string }[]): number {
  return new Set(orders.map((order) => JSON.stringify([order.storeId, order.orderId]))).size;
}

export function assessSalesConclusion(input: {
  primaryMetric: 'purchase' | 'revenue' | 'ctr';
  conversions: number;
  mature: boolean;
  trackingHealthy: boolean;
  comparisonValid: boolean;
  analysisSupportsConclusion: boolean;
}): PolicyDecision {
  const issues: PolicyIssue[] = [];
  if (input.primaryMetric === 'ctr') issues.push({ code: 'DIAGNOSTIC_ONLY', message: 'Clicks do not establish a sales winner.' });
  if (!Number.isFinite(input.conversions) || input.conversions <= 1) issues.push({ code: 'SPARSE_OUTCOMES', message: 'One purchase or fewer does not establish a winner.' });
  if (!input.mature || !input.trackingHealthy || !input.comparisonValid || !input.analysisSupportsConclusion) issues.push({ code: 'INSUFFICIENT_EVIDENCE', message: 'A mature, valid comparison and supported analysis are required.' });
  return { allowed: issues.length === 0, issues };
}
